import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

/**
 * TypeBadge
 * Small colored chip with an icon, used for category / event-type style
 * columns (e.g. GEOFENCE, ETA, RFID, Journey Type).
 *
 * `config` maps a value (case-insensitive) to { color, bg, icon }.
 * Any value not found in config falls back to `defaultConfig`.
 *
 * Usage:
 *   <TypeBadge
 *     value={row.eventType}
 *     config={{
 *       GEOFENCE: { color: "#1D6F42", bg: "#E9F9EE", icon: <LocationOnIcon fontSize="inherit" /> },
 *       ETA: { color: "#B45309", bg: "#FFF7E6", icon: <AccessTimeIcon fontSize="inherit" /> },
 *     }}
 *   />
 */
function TypeBadge({ value, config, defaultConfig }) {
  const key = typeof value === "string" ? value.trim().toUpperCase() : value;
  const normalizedConfig = Object.keys(config).reduce((acc, k) => {
    acc[k.toUpperCase()] = config[k];
    return acc;
  }, {});
  const { color, bg, icon } = normalizedConfig[key] || defaultConfig;

  return (
    <MDBox
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        px: "8px",
        py: "3px",
        borderRadius: "6px",
        backgroundColor: bg,
      }}
    >
      {icon && (
        <MDBox component="span" sx={{ display: "inline-flex", color, fontSize: "0.9rem" }}>
          {icon}
        </MDBox>
      )}
      <MDTypography variant="caption" fontWeight="bold" sx={{ color, fontSize: "0.7rem", letterSpacing: "0.02em" }}>
        {value || "-"}
      </MDTypography>
    </MDBox>
  );
}

TypeBadge.propTypes = {
  value: PropTypes.string,
  config: PropTypes.object,
  defaultConfig: PropTypes.shape({
    color: PropTypes.string,
    bg: PropTypes.string,
    icon: PropTypes.node,
  }),
};

TypeBadge.defaultProps = {
  value: "",
  config: {},
  defaultConfig: { color: "#5E6C84", bg: "#F1F2F4", icon: null },
};

export default TypeBadge;